import type { DayName, Schedule } from '@iheartjane/types';

import {
  convert12HourTimeTo24HourTime,
  convert24HourTimeTo12HourTime,
} from './ScheduleTimeValidation';

const DAYS: DayName[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

// Top-level date/time form fields the pickers bind to when creating a new
// schedule (or editing one with no stored schedule). Shared by the store/global
// specials and menu-row modals.
//
// Times start blank so the picker shows its "Select a start time" placeholder
// and the user has to choose one.
const EMPTY_SCHEDULE_FORM_FIELDS = {
  enabled_date_end_checked: false,
  enabled_date_start_checked: false,
  enabled_date_end: '',
  enabled_date_start: '',
  end_date: '',
  end_time: '',
  start_date: '',
  start_time: '',
  use_store_close_time: false,
};

// The default all-day, every-day schedule submitted when a special or menu row
// has no schedule overrides. Shared by the store/global specials and menu-row
// modals.
export const DEFAULT_SCHEDULE: Schedule = {
  enabled_sunday: true,
  enabled_monday: true,
  enabled_tuesday: true,
  enabled_wednesday: true,
  enabled_thursday: true,
  enabled_friday: true,
  enabled_saturday: true,
  enabled_sunday_all_day: true,
  enabled_monday_all_day: true,
  enabled_tuesday_all_day: true,
  enabled_wednesday_all_day: true,
  enabled_thursday_all_day: true,
  enabled_friday_all_day: true,
  enabled_saturday_all_day: true,
  end_time_sunday: null,
  start_time_sunday: null,
  end_time_monday: null,
  start_time_monday: null,
  end_time_tuesday: null,
  start_time_tuesday: null,
  end_time_wednesday: null,
  start_time_wednesday: null,
  end_time_thursday: null,
  start_time_thursday: null,
  end_time_friday: null,
  start_time_friday: null,
  end_time_saturday: null,
  start_time_saturday: null,
};

type ScheduleTimeKey = Extract<
  keyof Schedule,
  `start_time${string}` | `end_time${string}`
>;

const isTimeKey = (key: keyof Schedule): key is ScheduleTimeKey =>
  key.startsWith('start_time') || key.startsWith('end_time');

export const formatSchedule = (schedule: Schedule): Schedule => {
  const formatted: Schedule = { ...schedule };

  for (const key of Object.keys(formatted) as (keyof Schedule)[]) {
    if (isTimeKey(key)) {
      formatted[key] = convert12HourTimeTo24HourTime(formatted[key]);
    }
  }

  return formatted;
};

const hasScheduleOverrides = (schedule: Schedule, isCreateMode?: boolean) => {
  if (isCreateMode) {
    return false;
  }

  const allDayEveryDay = DAYS.every(
    (day) => schedule[`enabled_${day}`] && schedule[`enabled_${day}_all_day`]
  );

  const anyScheduleOverrides = DAYS.some(
    (day) => schedule[`start_time_${day}`] || schedule[`end_time_${day}`]
  );

  return !allDayEveryDay || anyScheduleOverrides;
};

// Normalizes a stored per-day schedule for the form: coerces enabled flags,
// derives "all day" when a day has no stored times, and converts stored 24-hour
// times to the 12-hour values the pickers display.
const buildDayEntries = (schedule: Schedule) =>
  DAYS.reduce(
    (acc: Record<string, boolean | string>, day) => ({
      ...acc,
      [`enabled_${day}`]: schedule[`enabled_${day}`] || false,
      [`enabled_${day}_all_day`]:
        schedule[`enabled_${day}_all_day`] ||
        (!schedule[`end_time_${day}`] && !schedule[`start_time_${day}`]) ||
        false,
      [`start_time_${day}`]:
        convert24HourTimeTo12HourTime(schedule[`start_time_${day}`]) || '',
      [`end_time_${day}`]:
        convert24HourTimeTo12HourTime(schedule[`end_time_${day}`]) || '',
    }),
    {}
  );

// The per-day schedule plus the top-level start/end datetime strings, pulled
// from whichever domain object the caller has. Store/global specials keep
// `start_time`/`end_time` on the special itself; menu rows keep them inside
// `row.schedule`. Callers normalize to this shape so one parser serves both.
type ParseScheduleInput = {
  endTime?: string | null;
  schedule?: Schedule;
  startTime?: string | null;
};

export const parseSchedule = (
  { schedule, startTime, endTime }: ParseScheduleInput,
  isCreateMode?: boolean
) => {
  if (isCreateMode || !schedule) {
    const createSchedule = {
      ...DAYS.reduce(
        (acc: Record<string, boolean | string>, day) => ({
          ...acc,
          [`enabled_${day}`]: true,
          [`enabled_${day}_all_day`]: true,
          [`start_time_${day}`]: '',
          [`end_time_${day}`]: '',
        }),
        {}
      ),
      use_store_close_time_for_end: false,
    };
    return {
      schedule: createSchedule,
      has_schedule_overrides: hasScheduleOverrides(createSchedule, true),
      ...EMPTY_SCHEDULE_FORM_FIELDS,
    };
  }

  const fullEndDate = endTime ? endTime.split(' ') : [];
  const fullStartDate = startTime ? startTime.split(' ') : [];
  const [endDate, endTimeOfDay] = fullEndDate;
  const [startDate, startTimeOfDay] = fullStartDate;

  const parsedSchedule = {
    ...buildDayEntries(schedule),
    use_store_close_time_for_end:
      schedule.use_store_close_time_for_end || false,
  };
  return {
    schedule: parsedSchedule,
    has_schedule_overrides: hasScheduleOverrides(parsedSchedule, false),
    enabled_date_start_checked: fullStartDate.filter(Boolean).length > 1,
    enabled_date_end_checked: fullEndDate.filter(Boolean).length > 1,
    end_date: endDate,
    // A schedule with no stored time stays blank so the picker prompts for one.
    // convert24HourTimeTo12HourTime already returns '' for a missing value.
    end_time: convert24HourTimeTo12HourTime(endTimeOfDay),
    start_date: startDate,
    start_time: convert24HourTimeTo12HourTime(startTimeOfDay),
    enabled_date_end: '',
    enabled_date_start: '',
    use_store_close_time: schedule.use_store_close_time_for_end || false,
  };
};
